import { apiClient } from './client';
import { API_ENDPOINTS } from './config';

export interface CreatePaymentIntentRequest {
  amount: number; // Amount in cents
  currency?: string;
  planId?: string;
  /** If true, subscribe current workspace (uses user currentWorkspaceId). */
  forWorkspace?: boolean;
}

export interface CreatePaymentIntentResponse {
  clientSecret: string;
  paymentIntentId: string;
}

export interface Payment {
  id: string;
  userId: string;
  stripePaymentIntentId: string;
  stripeCustomerId: string;
  amount: number;
  currency: string;
  status: 'pending' | 'processing' | 'succeeded' | 'failed' | 'canceled';
  planId: string | null;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Subscription {
  id: string;
  userId: string;
  stripeSubscriptionId: string;
  stripeCustomerId: string;
  planId: string;
  status: 'active' | 'canceled' | 'past_due' | 'unpaid' | 'trialing';
  interval: 'month' | 'year';
  amount: number;
  currency: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  trialStart: string | null;
  trialEnd: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Invoice {
  id: string;
  amount: number;
  currency: string;
  status: 'paid' | 'open' | 'void' | 'uncollectible';
  invoicePdf: string | null;
  hostedInvoiceUrl: string | null;
  created: number;
  periodStart: number | null;
  periodEnd: number | null;
}

export interface CancelSubscriptionRequest {
  cancelAtPeriodEnd?: boolean;
}

export interface ChangePlanRequest {
  planId: string;
  prorate?: boolean;
}

export const paymentsApi = {
  /**
   * Create a payment intent for checkout
   */
  async createPaymentIntent(
    data: CreatePaymentIntentRequest,
  ): Promise<CreatePaymentIntentResponse> {
    return apiClient.post<CreatePaymentIntentResponse>(
      API_ENDPOINTS.PAYMENTS.CREATE_INTENT,
      data,
    );
  },

  /**
   * Get payment history for current user
   */
  async getPaymentHistory(): Promise<Payment[]> {
    return apiClient.get<Payment[]>(API_ENDPOINTS.PAYMENTS.HISTORY);
  },

  /**
   * Get current user subscription
   */
  async getSubscription(): Promise<Subscription | null> {
    return apiClient.get<Subscription | null>(API_ENDPOINTS.PAYMENTS.SUBSCRIPTION);
  },

  /**
   * Get invoices for current user
   */
  async getInvoices(): Promise<Invoice[]> {
    return apiClient.get<Invoice[]>(API_ENDPOINTS.PAYMENTS.INVOICES);
  },

  /**
   * Cancel current user subscription
   */
  async cancelSubscription(
    data: CancelSubscriptionRequest,
  ): Promise<{ message: string; subscription: Subscription }> {
    return apiClient.post<{ message: string; subscription: Subscription }>(
      API_ENDPOINTS.PAYMENTS.CANCEL,
      data,
    );
  },

  /**
   * Upgrade to a higher plan
   */
  async upgradePlan(
    data: ChangePlanRequest,
  ): Promise<{ message: string; subscription: Subscription }> {
    return apiClient.post<{ message: string; subscription: Subscription }>(
      API_ENDPOINTS.PAYMENTS.UPGRADE,
      data,
    );
  },

  /**
   * Downgrade to a lower plan
   */
  async downgradePlan(
    data: ChangePlanRequest,
  ): Promise<{ message: string; subscription: Subscription }> {
    return apiClient.post<{ message: string; subscription: Subscription }>(
      API_ENDPOINTS.PAYMENTS.DOWNGRADE,
      data,
    );
  },

  /**
   * Reactivate a canceled subscription
   */
  async reactivateSubscription(): Promise<{ message: string; subscription: Subscription }> {
    return apiClient.post<{ message: string; subscription: Subscription }>(
      API_ENDPOINTS.PAYMENTS.REACTIVATE,
      {},
    );
  },

  /**
   * Poll payment intent status until it's succeeded or failed
   * This is useful for checking payment status after checkout
   */
  async pollPaymentStatus(
    paymentIntentId: string,
    maxAttempts: number = 30,
    intervalMs: number = 2000,
  ): Promise<'succeeded' | 'failed' | 'timeout'> {
    // Note: We don't have a direct endpoint to get payment intent status
    // Instead, we'll check the payment history or use webhooks
    // For now, we'll return a timeout and rely on webhooks
    return new Promise((resolve) => {
      let attempts = 0;
      const checkStatus = async () => {
        attempts++;
        try {
          const history = await this.getPaymentHistory();
          const payment = history.find(
            (p) => p.stripePaymentIntentId === paymentIntentId,
          );
          if (payment) {
            if (payment.status === 'succeeded') {
              resolve('succeeded');
              return;
            }
            if (payment.status === 'failed' || payment.status === 'canceled') {
              resolve('failed');
              return;
            }
          }
          if (attempts >= maxAttempts) {
            resolve('timeout');
            return;
          }
          setTimeout(checkStatus, intervalMs);
        } catch (error) {
          if (attempts >= maxAttempts) {
            resolve('timeout');
            return;
          }
          setTimeout(checkStatus, intervalMs);
        }
      };
      checkStatus();
    });
  },
};
